import { TuiNotificationService } from "@taiga-ui/core";
import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { Location } from '@angular/common';
import { AuthService } from '../../features/auth/services/auth.service';
import { PermissionsService } from '../auth/permissions.service';
import { MatchStoreService } from '../../features/matches/services/match-store.service';
import { MatchAccessTokenService } from '../services/match-access-token.service';
import { showUnauthorizedAlert } from '../utils/alert-helper.util';
import { map, catchError, of, switchMap, take, filter, Observable } from 'rxjs';
import { NavigationEnd } from '@angular/router';
import { TournamentUserRoleService } from '../../features/tournaments/services/tournament-user-role.service';

export const scoreboardAdminGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const location = inject(Location);
  const alerts = inject(TuiNotificationService);
  const matchStore = inject(MatchStoreService);
  const accessTokenService = inject(MatchAccessTokenService);
  const tournamentRoleService = inject(TournamentUserRoleService);
  const permissions = inject(PermissionsService);

  const matchIdStr = route.paramMap.get('matchId');
  const matchIdNum = Number(matchIdStr);

  // --- Access token path (no account required) ---
  // Token may come from the share URL (?access_token=…) on first visit, or
  // from sessionStorage on a page reload (the URL was stripped clean).
  const queryToken = route.queryParamMap.get('access_token');
  const storedToken =
    !queryToken && Number.isFinite(matchIdNum)
      ? accessTokenService.getStoredToken(matchIdNum)
      : null;
  const accessToken = queryToken ?? storedToken;

  if (accessToken) {
    return accessTokenService.validateAndStore(accessToken).pipe(
      switchMap((validMatchId) => {
        if (validMatchId !== null && validMatchId === matchIdNum) {
          // Only the URL-supplied token needs stripping; a stored-token reload
          // already has a clean URL.
          if (queryToken) {
            // Strip access_token from the URL after the router commits the navigation.
            // We subscribe to the next NavigationEnd event — that fires after the router
            // has already called history.pushState, so our replaceState wins.
            const targetPath = '/' + route.url.map((s) => s.path).join('/');
            const remainingParams = route.queryParamMap.keys
              .filter((k) => k !== 'access_token')
              .reduce<Record<string, string>>((acc, k) => {
                const v = route.queryParamMap.get(k);
                if (v !== null) acc[k] = v;
                return acc;
              }, {});
            const qs = new URLSearchParams(remainingParams).toString();
            const cleanUrl = targetPath + (qs ? `?${qs}` : '');
            router.events
              .pipe(
                filter((e) => e instanceof NavigationEnd),
                take(1),
              )
              .subscribe(() => location.replaceState(cleanUrl));
          }
          return of<boolean | UrlTree>(true);
        }
        // A stale stored token (expired/revoked) should not hard-block — drop
        // it and fall back to the standard JWT path.
        if (storedToken) {
          accessTokenService.clearStoredToken(matchIdNum);
          return jwtAuthPath();
        }
        showUnauthorizedAlert(alerts, 'forbidden');
        return of<boolean | UrlTree>(router.createUrlTree(['/home']));
      })
    );
  }

  return jwtAuthPath();

  // --- Standard JWT auth path ---
  function jwtAuthPath(): Observable<boolean | UrlTree> {
    // Capture trigger synchronously — getCurrentNavigation() is null after async resolves.
    const trigger = router.getCurrentNavigation()?.trigger;

    return authService.ensureValidSession().pipe(
      switchMap((): Observable<boolean | UrlTree> => {
        // Read signals AFTER validation. On a real 401/403 the session was cleared
        // (expireSession); on transient /me errors the cached signal is preserved by
        // design (fail-open), so admin/editor/stats_editor may still be granted within the TTL window.
        const currentUser = authService.currentUser();
        const isAuthenticated = authService.isAuthenticated();

        if (isAuthenticated && permissions.canEdit()) {
          return of<boolean | UrlTree>(true);
        }

        if (!matchIdStr) {
          showUnauthorizedAlert(alerts, isAuthenticated ? 'forbidden' : 'unauthenticated', {
            sessionExpiredRecently: authService.sessionExpiredRecently(),
          });
          if (trigger === 'imperative') {
            return of<boolean | UrlTree>(false);
          }
          return of<boolean | UrlTree>(router.createUrlTree(['/home']));
        }

        // Fetch match data — needed for public_scoreboard, owner, and tournament role checks
        return matchStore.getComprehensiveMatchData(matchIdNum).pipe(
          switchMap((data) => {
            // Public scoreboard bypass — no auth required. Accept all three tournament shapes
            // (top-level REST `tournament`, WS-nested `tournament`, legacy `tournaments`).
            const dataRaw = data as unknown as Record<string, unknown>;
            const matchRaw = data.match as unknown as Record<string, unknown>;
            const tournament = (matchRaw['tournament'] ??
              matchRaw['tournaments'] ??
              dataRaw['tournament']) as { public_scoreboard?: boolean } | null;
            if (tournament?.public_scoreboard) {
              return of<boolean | UrlTree>(true);
            }

            // From here on, authentication is required
            if (!isAuthenticated) {
              showUnauthorizedAlert(alerts, 'unauthenticated', {
                sessionExpiredRecently: authService.sessionExpiredRecently(),
              });
              if (trigger === 'imperative') {
                return of<boolean | UrlTree>(false);
              }
              return of<boolean | UrlTree>(router.createUrlTree(['/home']));
            }

            // Owner check
            const matchUserId = data.match.user_id;
            if (matchUserId === currentUser?.id) {
              return of<boolean | UrlTree>(true);
            }

            // Fallback: check tournament-scoped permission
            const tournamentId = data.match.tournament_id;
            if (tournamentId && currentUser) {
              return tournamentRoleService.getMyPermissions(tournamentId).pipe(
                map((permissions) => {
                  if (permissions.includes('match_admin')) {
                    return true as boolean | UrlTree;
                  }
                  showUnauthorizedAlert(alerts, 'forbidden');
                  if (trigger === 'imperative') {
                    return false as boolean | UrlTree;
                  }
                  return router.createUrlTree(['/home']);
                }),
                catchError(() => {
                  showUnauthorizedAlert(alerts, 'forbidden');
                  return of<boolean | UrlTree>(router.createUrlTree(['/home']));
                }),
              );
            }

            showUnauthorizedAlert(alerts, 'forbidden');
            if (trigger === 'imperative') {
              return of<boolean | UrlTree>(false);
            }
            return of<boolean | UrlTree>(router.createUrlTree(['/home']));
          }),
          catchError(() => {
            showUnauthorizedAlert(alerts, isAuthenticated ? 'forbidden' : 'unauthenticated', {
              sessionExpiredRecently: authService.sessionExpiredRecently(),
            });
            return of<boolean | UrlTree>(router.createUrlTree(['/home']));
          }),
        );
      }),
    );
  }
};
